import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { TagsListWithTagsEditor } from '../../../components/entities/tags/TagsEditor';
import { TagIcon } from '../../../components/svgs/icons/TagIcon';
import { navigation } from '../../../styles/colors';
import { bs } from '../../../styles/styles';

interface Props {
	companyVm: Api.CompanyViewModel;
}

function _CompanyInfoTags({ companyVm }: Props) {
	const [tags, setTags] = React.useState<Api.IAccountTag[] | null>(null);
	const errorMessages = useErrorMessages();
	const { logEvent } = useEventLogging('ContactInfoKeyFactsAndTags');
	const updateTags = (newTags: Api.IAccountTag[], eventName: string) => {
		const promise = companyVm.updateTags(newTags.map(x => x.name));
		if (promise) {
			logEvent(eventName, { count: newTags.length });
			// set override
			setTags(newTags);
			promise
				.then(() => {
					setTags(null);
				})
				.catch((error: Api.IOperationResultNoValue) => {
					setTags(null);
					logEvent(`${eventName}-Error`, { ...error });
					errorMessages.pushApiError(error);
				});
		}
	};
	const onRequestAddTag = (accountTag: Api.IAccountTag) => {
		if (
			companyVm.tags?.length &&
			accountTag?.name &&
			companyVm.tags.find(x => x.toLocaleLowerCase() === accountTag.name.toLocaleLowerCase())
		) {
			return;
		}
		const newTags: Api.IAccountTag[] = [...(companyVm.tags.map(name => ({ name })) || [])];
		newTags.push(accountTag);
		updateTags(newTags, 'AddTag');
	};
	const onRequestRemoveTag = (accountTag: Api.IAccountTag) => {
		const newTags = [...(companyVm.tags.map(name => ({ name })) || [])];
		const index = newTags.findIndex(x => x.name === accountTag.name);
		if (index >= 0) {
			newTags.splice(index, 1);
			updateTags(newTags, 'RemoveTag');
		}
	};

	// If `tags` are passed in they take precedence, else we use the tagsCollection if it exists
	// else we use the tags array
	const companyTags = tags ?? companyVm.tagsCollection ?? (companyVm.tags?.map(name => ({ name })) || []);
	return (
		<div className={css(bs.flex, bs.justifyStart)}>
			<TagIcon className={css(bs.flexShrink0, bs.mr4, bs.mt3, bs.w4)} fillColor={navigation} />
			<TagsListWithTagsEditor
				tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
				className={css(bs.flex1)}
				disabled={!!companyVm.isBusy}
				entity={companyVm}
				onRequestAddTag={onRequestAddTag}
				onRequestRemoveTag={onRequestRemoveTag}
				tags={companyTags}
				isCompanyTags
			/>
		</div>
	);
}

export const CompanyInfoTags = observer(_CompanyInfoTags);
